import type { TodayApproval } from './todayData';
import { approverPhrase } from './approver';

/**
 * Today → Went out: one row per page, article, form or the site, however
 * many times it was approved in the window. The row says which version is
 * live and how many times it went out; the versions are behind History.
 */
export interface WentOutGroup {
  key: string;
  /** Newest first. */
  events: TodayApproval[];
  latest: TodayApproval;
  /** The publish that is live now, if one in the window still is. */
  live: TodayApproval | null;
  publishes: number;
}

export function groupApprovals(approvals: TodayApproval[]): WentOutGroup[] {
  const byKey = new Map<string, TodayApproval[]>();
  for (const a of approvals) {
    const key = `${a.kind}:${a.subjectId}`;
    byKey.set(key, [...(byKey.get(key) || []), a]);
  }
  return [...byKey].map(([key, list]) => {
    const events = [...list].sort(
      (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
    );
    return {
      key,
      events,
      latest: events[0],
      live: events.find((e) => e.action === 'publish' && e.stillLive) || null,
      publishes: events.filter((e) => e.action === 'publish').length,
    };
  });
}

const version = (a: TodayApproval) => (a.versionNumber ? `v${a.versionNumber}` : null);

/** "v17 live · published 6× today", for a group of more than one approval. */
export function groupSummary(g: WentOutGroup, period: string) {
  const live = g.live ? version(g.live) : null;
  const count =
    g.publishes === g.events.length
      ? `published ${g.publishes}× ${period}`
      : `${g.events.length} approvals ${period}`;
  return [live ? `${live} live` : null, count].filter(Boolean).join(' · ');
}

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};

/** One line of a group's History: when, which version, what, and whether it's live. */
export function historyLine(a: TodayApproval) {
  return {
    id: a.id,
    at: new Date(a.recordedAt),
    version: version(a) || '—',
    what: [VERB[a.action] || a.action, a.approvedBy ? `by ${approverPhrase(a)}` : null]
      .filter(Boolean)
      .join(' '),
    status:
      a.action !== 'publish' ? null : a.stillLive ? ('Live' as const) : ('No longer live' as const),
  };
}

// ── Row words (round 5: every row says what it is and where it lives) ────────

const KIND_NOUN: Record<string, string> = {
  article: 'the article',
  page: 'the page',
  form: 'the form',
  dataset: 'the data for',
};

/**
 * "Published the page “Tips”", "Updated the article “…”", "Took down the form
 * “Tips”", "Published the site’s look and navigation". A publish of a version
 * after the first is an update. `updated` overrides for a group of several.
 */
export function rowText(a: TodayApproval, updated?: boolean): string {
  if (a.kind === 'site') {
    return a.action === 'unpublish'
      ? 'Took down the site’s look and navigation'
      : `${a.action === 'schedule' ? 'Scheduled' : 'Published'} the site’s look and navigation`;
  }
  const isUpdate = updated ?? (a.action === 'publish' && (a.versionNumber || 0) > 1);
  const verb =
    a.action === 'unpublish'
      ? 'Took down'
      : a.action === 'schedule'
        ? 'Scheduled'
        : isUpdate
          ? 'Updated'
          : 'Published';
  const noun = KIND_NOUN[a.kind] || `the ${a.kind}`;
  return a.subjectTitle
    ? `${verb} ${noun} “${a.subjectTitle}”`
    : `${verb} ${noun.replace(/^the /, 'a ')}`;
}

/**
 * Where it lives: "/tips" for a page or article; "on /tips" (or "on /tips and
 * /contact") for a form, which lives on the pages that show it; "not on a live
 * page" for a form no live page shows; "site-wide" for the site's look. The
 * home page's address is a bare "/", which means nothing to most owners
 * (backlog f42e5a87): its row already names the page, so it's left out, and a
 * form on it is "on the home page".
 */
export function rowPlace(a: TodayApproval): string | null {
  if (a.kind === 'site') return 'site-wide';
  if (a.kind === 'form') {
    const on = (a.placedOn || []).map((p) => (p === '/' ? 'the home page' : p));
    if (!on.length) return 'not on a live page';
    const shown = on.slice(0, 2).join(' and ');
    return `on ${shown}${on.length > 2 ? ` and ${on.length - 2} more` : ''}`;
  }
  return a.subjectPath && a.subjectPath !== '/' ? a.subjectPath : null;
}

// ── Under your rules (backlog 0ebe4e50) ──────────────────────────────────────

/**
 * A sent post that went out without anyone approving it: an agent, a
 * connection or an always-on campaign made it, and it was scheduled straight
 * away because its channel was set to "Goes out without asking" (posts.js). A
 * person's own post and an approved campaign's posts carry review_status
 * 'approved'; older rows without createdVia aren't claimed either way.
 */
export function wentOutByRule(p: { createdVia?: string | null; reviewStatus?: string | null }) {
  return Boolean(p.createdVia && p.createdVia !== 'app' && !p.reviewStatus);
}

/**
 * "Approved by Hai": a machine's post a person let out, by approving it or
 * sending it again after it didn't go out (backlog 9f28ddba). A person's own
 * post goes unsaid.
 */
export function postApprovedBy(p: {
  createdVia?: string | null;
  reviewStatus?: string | null;
  reviewerName?: string | null;
}) {
  return p.createdVia && p.createdVia !== 'app' && p.reviewStatus === 'approved' && p.reviewerName
    ? `Approved by ${p.reviewerName}`
    : null;
}

/** "X goes out without asking": the setting as Account → Approvals names it. */
export const postRuleLabel = (channel: string) => `${channel} goes out without asking`;
